/*  AFP — A Few Pieces
 *  assets/codice-fiscale-validator.js
 *
 *  Dichiarazione del paese di fatturazione (Italia / Estero) e raccolta del
 *  Codice Fiscale / P.IVA nel carrello. Salva due attributi carrello e trattiene
 *  i pulsanti di checkout del carrello finché Shopify non conferma il
 *  salvataggio. È una guida lato carrello, non un vincolo sul checkout.
 *
 *  CARICAMENTO — una sola volta, FUORI dalla sezione carrello.
 *  In layout/theme.liquid, subito prima di </head>:
 *
 *      <script src="{{ 'codice-fiscale-validator.js' | asset_url }}" defer></script>
 *
 *  Non inserire lo script dentro snippets/cart-summary.liquid: quel markup viene
 *  rigenerato dal morphing del tema, e un tag <script> reinserito dal morphing
 *  non viene mai eseguito dal browser.
 */

(function () {
  'use strict';

  /* Se il file venisse incluso due volte, la seconda esecuzione esce subito:
     niente doppia define, niente doppi listener globali. */
  if (window.__afpFiscalValidatorLoaded) return;
  window.__afpFiscalValidatorLoaded = true;

  /* ------------------------------------------------------------ impostazioni */

  /* Verifica il carattere di controllo del codice fiscale e la cifra di
     controllo della partita IVA, non solo la forma. Mettere a false per
     accettare qualunque codice formalmente corretto. */
  const VERIFICA_CARATTERE_DI_CONTROLLO = true;

  /* Se dopo tutti i tentativi il salvataggio non riesce (rete, 429, 500) e il
     dato inserito è comunque valido, il cliente viene lasciato proseguire.
     Bloccare un ordine per un errore nostro costa più che chiedere il dato dopo.
     Mettere a false per trattenere il checkout a oltranza. */
  const SBLOCCA_SE_IL_SALVATAGGIO_FALLISCE = true;

  const TENTATIVI_DI_SALVATAGGIO = 3;
  const ATTESA_TRA_I_TENTATIVI = [400, 1200];
  const RITARDO_SALVATAGGIO = 350;

  /* ---------------------------------------------------------------- costanti */

  const TAG = 'codice-fiscale-validator';
  const KEY = 'Codice Fiscale / P.IVA';
  const BILLING_KEY = 'Paese di fatturazione dichiarato';
  const ITALIA = 'Italia';
  const ESTERO = 'Estero';
  const CHOICES = [ITALIA, ESTERO];

  const CTA_SCOPE_SELECTOR = '[data-fiscal-ctas]';
  const CHECKOUT_SELECTOR = '[name="checkout"], #checkout';
  const ACCEL_SELECTOR = [
    '.additional-checkout-buttons',
    '.dynamic-checkout__content',
    '.cart__dynamic-checkout-buttons',
    'shopify-accelerated-checkout',
    'shopify-accelerated-checkout-cart',
    '[data-shopify="dynamic-checkout-cart"]'
  ].join(', ');

  const LOCK_ATTR = 'data-afp-fiscal-lock';

  /* Codice fiscale persona fisica, incluse le sostituzioni di lettera previste
     per l'omocodia. Partita IVA o codice fiscale numerico: 11 cifre. */
  const CF_PATTERN = /^[A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/;
  const PIVA_PATTERN = /^[0-9]{11}$/;

  /* Tabella dei valori per i caratteri in posizione dispari (1a, 3a, 5a…).
     I caratteri in posizione pari valgono 0-9 per le cifre e 0-25 per A-Z. */
  const VALORI_DISPARI = {
    0: 1, 1: 0, 2: 5, 3: 7, 4: 9, 5: 13, 6: 15, 7: 17, 8: 19, 9: 21,
    A: 1, B: 0, C: 5, D: 7, E: 9, F: 13, G: 15, H: 17, I: 19, J: 21,
    K: 2, L: 4, M: 18, N: 20, O: 11, P: 3, Q: 6, R: 8, S: 12, T: 14,
    U: 16, V: 10, W: 22, X: 25, Y: 24, Z: 23
  };

  /* ------------------------------------------------------------- funzioni utili */

  const attesa = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  function valorePari(carattere) {
    const code = carattere.charCodeAt(0);
    return code >= 48 && code <= 57 ? code - 48 : code - 65;
  }

  function carattereDiControllo(primi15) {
    let somma = 0;
    for (let i = 0; i < 15; i++) {
      const carattere = primi15[i];
      somma += i % 2 === 0 ? VALORI_DISPARI[carattere] : valorePari(carattere);
    }
    return String.fromCharCode(65 + (somma % 26));
  }

  function codiceFiscaleValido(value) {
    if (!CF_PATTERN.test(value)) return false;
    if (!VERIFICA_CARATTERE_DI_CONTROLLO) return true;
    return carattereDiControllo(value.slice(0, 15)) === value[15];
  }

  function partitaIvaValida(value) {
    if (!PIVA_PATTERN.test(value)) return false;
    if (!VERIFICA_CARATTERE_DI_CONTROLLO) return true;
    if (value === '00000000000') return false;
    let somma = 0;
    for (let i = 0; i < 11; i++) {
      let cifra = value.charCodeAt(i) - 48;
      if (i % 2 === 1) {
        cifra *= 2;
        if (cifra > 9) cifra -= 9;
      }
      somma += cifra;
    }
    return somma % 10 === 0;
  }

  /* Rete di sicurezza CSS: garantisce che [hidden] vinca anche se una regola del
     tema imposta display su questi elementi. Senza, il campo potrebbe restare
     visibile con Estero selezionato, o i pulsanti rapidi restare cliccabili. */
  function injectStyle() {
    if (document.getElementById('afp-fiscal-style')) return;
    const style = document.createElement('style');
    style.id = 'afp-fiscal-style';
    style.textContent =
      `${TAG} [hidden], ${TAG}[hidden] { display: none !important; }\n` +
      `[${LOCK_ATTR}][hidden] { display: none !important; }`;
    (document.head || document.documentElement).appendChild(style);
  }

  function normaliseChoice(raw) {
    const value = String(raw ?? '').trim();
    return CHOICES.find((choice) => choice.toLowerCase() === value.toLowerCase()) || '';
  }

  function normaliseCode(raw) {
    return String(raw ?? '').replace(/\s+/g, '').toUpperCase();
  }

  function isItalian() {
    /* I testi fiscali seguono il mercato/paese selezionato, non la lingua del
       browser: Italia in italiano, tutti gli altri paesi in inglese. */
    return (document.documentElement.dataset.marketCountry || '').toUpperCase() === 'IT';
  }

  /* Scrivere solo quando il valore cambia davvero: evita mutazioni inutili che
     rientrerebbero nel MutationObserver. */
  function setText(node, value) {
    if (node && node.textContent !== value) node.textContent = value;
  }

  function setClass(node, name, on) {
    if (node && node.classList.contains(name) !== !!on) node.classList.toggle(name, !!on);
  }

  function setAttr(node, name, value) {
    if (!node) return;
    if (value === null) {
      if (node.hasAttribute(name)) node.removeAttribute(name);
    } else if (node.getAttribute(name) !== value) {
      node.setAttribute(name, value);
    }
  }

  class CodiceFiscaleValidator extends HTMLElement {
    static KEY = KEY;
    static BILLING_KEY = BILLING_KEY;

    static instances = new Set();
    static billing = '';
    static value = '';
    static ready = false;
    static busy = false;
    static failed = false;
    static mode = 'save';
    static seeded = false;
    static saved = { value: null, billing: null };
    static revision = 0;
    static queue = Promise.resolve();
    static timer = null;
    static installed = false;
    static bodyFrame = 0;

    /* -------------------------------------------------------------- validità */

    static valid(value) {
      return codiceFiscaleValido(value) || partitaIvaValida(value);
    }

    static acceptable(value, billing) {
      if (billing === ESTERO) return true;
      if (billing === ITALIA) return CodiceFiscaleValidator.valid(value);
      return false;
    }

    /* -------------------------------------------------------- lettura del DOM */

    static hydrated(root) {
      return !!(root.querySelector('[data-fiscal-billing]') && root.querySelector('[data-fiscal-input]'));
    }

    /* Il markup attuale usa una <select>. Queste due funzioni accettano anche un
       gruppo di radio o un contenitore, così un cambio di markup non rompe di
       nuovo il componente. */
    static readBilling(root) {
      for (const node of root.querySelectorAll('[data-fiscal-billing]')) {
        if (node.tagName === 'SELECT') return normaliseChoice(node.value);
        if (node.tagName === 'INPUT') {
          if (node.type === 'radio' || node.type === 'checkbox') {
            if (node.checked) return normaliseChoice(node.value);
            continue;
          }
          return normaliseChoice(node.value);
        }
        const checked = node.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
        if (checked) return normaliseChoice(checked.value);
        const select = node.querySelector('select');
        if (select) return normaliseChoice(select.value);
      }
      return '';
    }

    static writeBilling(root, value) {
      for (const node of root.querySelectorAll('[data-fiscal-billing]')) {
        if (node.tagName === 'SELECT') {
          if (normaliseChoice(node.value) !== value) node.value = value;
          continue;
        }
        if (node.tagName === 'INPUT') {
          if (node.type === 'radio' || node.type === 'checkbox') {
            const should = value !== '' && normaliseChoice(node.value) === value;
            if (node.checked !== should) node.checked = should;
          } else if (normaliseChoice(node.value) !== value) {
            node.value = value;
          }
          continue;
        }
        for (const radio of node.querySelectorAll('input[type="radio"], input[type="checkbox"]')) {
          const should = value !== '' && normaliseChoice(radio.value) === value;
          if (radio.checked !== should) radio.checked = should;
        }
        const select = node.querySelector('select');
        if (select && normaliseChoice(select.value) !== value) select.value = value;
      }
    }

    /* ------------------------------------------------------------------ rete */

    static async request(method, value, billing) {
      let root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      if (!root.endsWith('/')) root += '/';

      const options = { method, credentials: 'same-origin', cache: 'no-store' };
      if (method === 'POST') {
        options.headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
        options.body = JSON.stringify({ attributes: { [KEY]: value, [BILLING_KEY]: billing } });
      }

      const response = await fetch(root + (method === 'POST' ? 'cart/update.js' : 'cart.js'), options);
      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
      return response.json();
    }

    /* Shopify rimuove l'attributo quando il valore inviato è vuoto: leggendo la
       risposta, attributo assente e stringa vuota devono coincidere. */
    static readAttribute(cart, key) {
      return String(cart?.attributes?.[key] ?? '');
    }

    /* ----------------------------------------------------------- ciclo di vita */

    connectedCallback() {
      injectStyle();

      const C = CodiceFiscaleValidator;

      /* L'istanza si registra SEMPRE, anche se i figli non ci sono ancora (script
         non differito, oppure morphing che inserisce il nodo e poi lo riempie).
         Uscire qui lascerebbe instances vuoto e sbloccherebbe il checkout in
         silenzio: il fallimento deve andare nella direzione prudente. */
      C.instances.add(this);

      /* I riferimenti ai nodi non vengono mai memorizzati. Il morphing del tema
         sostituisce i figli senza far ripartire connectedCallback, e i nodi
         salvati resterebbero scollegati dalla pagina: è questo che lasciava il
         campo morto dopo un aggiornamento del carrello. I listener stanno sul
         custom element, che il morphing conserva. */
      if (!this._bound) {
        this._bound = true;
        this.addEventListener('change', this.handleChange.bind(this));
        this.addEventListener('input', this.handleInput.bind(this));
        this.addEventListener('focusout', this.handleFocusOut.bind(this));
      }

      /* Se il tema rigenera i figli, o se i figli arrivano dopo, si riallinea. */
      if (!this._observer) {
        this._observer = new MutationObserver(() => {
          if (this._frame) return;
          this._frame = requestAnimationFrame(() => {
            this._frame = 0;
            C.seed();
            C.render();
          });
        });
      }
      this._observer.observe(this, { childList: true, subtree: true });

      C.seed();
      C.install();
      C.render();
    }

    disconnectedCallback() {
      this._observer?.disconnect();
      if (this._frame) {
        cancelAnimationFrame(this._frame);
        this._frame = 0;
      }
      CodiceFiscaleValidator.instances.delete(this);
      CodiceFiscaleValidator.buttons();
    }

    /* Primo innesco: lo stato iniziale arriva dal markup, che Liquid ha già reso
       a partire da cart.attributes. Nessuno sfarfallio in attesa di rete, e
       nessuna richiesta inutile per riscrivere ciò che è già salvato. */
    static seed() {
      const C = CodiceFiscaleValidator;
      if (C.seeded) return;
      for (const root of C.instances) {
        if (!C.hydrated(root)) continue;
        C.seeded = true;
        C.billing = C.readBilling(root);
        C.value = normaliseCode(root.querySelector('[data-fiscal-input]')?.value);
        C.ready = C.acceptable(C.value, C.billing);
        C.saved = { value: C.ready ? C.value : null, billing: C.ready ? C.billing : null };
        return;
      }
    }

    /* --------------------------------------------------------------- eventi */

    handleChange(event) {
      const target = event.target;
      if (!target?.closest) return;
      const C = CodiceFiscaleValidator;
      if (target.closest('[data-fiscal-billing]')) {
        C.edit(C.value, C.readBilling(this));
      } else if (target.matches('[data-fiscal-input]')) {
        C.edit(target.value, C.billing);
      }
    }

    handleInput(event) {
      const target = event.target;
      if (!target?.matches?.('[data-fiscal-input]')) return;
      CodiceFiscaleValidator.edit(target.value, CodiceFiscaleValidator.billing);
    }

    handleFocusOut(event) {
      const target = event.target;
      if (!target?.matches?.('[data-fiscal-input]')) return;
      const C = CodiceFiscaleValidator;
      /* Uscendo dal campo si normalizza ciò che si vede e, se il salvataggio era
         fallito, si riprova. */
      C.render();
      if (C.failed) C.edit(target.value, C.billing);
    }

    /* ------------------------------------------------------------- scritture */

    static edit(raw, billing) {
      const C = CodiceFiscaleValidator;
      const chosen = normaliseChoice(billing);
      const code = chosen === ITALIA ? normaliseCode(raw) : '';

      C.billing = chosen;
      C.value = code;
      C.ready = false;
      C.busy = false;
      C.failed = false;
      C.mode = 'save';

      const revision = ++C.revision;
      /* Si salva solo un codice valido: un valore parziale non deve finire negli
         attributi dell'ordine. */
      const payload = chosen === ITALIA && C.valid(code) ? code : '';

      clearTimeout(C.timer);
      C.render();

      C.timer = setTimeout(() => {
        if (revision !== C.revision) return;

        /* Niente richiesta se lo stato confermato è già quello: digitando un
           codice incompleto si invierebbe altrimenti una POST identica a ogni
           pausa, e ognuna cancellerebbe e riscriverebbe lo stesso attributo. */
        if (payload === C.saved.value && chosen === C.saved.billing) {
          C.ready = C.acceptable(payload, chosen);
          C.render();
          return;
        }

        C.busy = true;
        C.render();

        C.queue = C.queue.then(async () => {
          if (revision !== C.revision) return;
          const saved = await C.persist(payload, chosen, revision);
          if (revision !== C.revision) return;

          if (saved) {
            C.saved = { value: payload, billing: chosen };
            C.ready = C.acceptable(payload, chosen);
            C.failed = false;
          } else {
            C.failed = true;
            C.ready = SBLOCCA_SE_IL_SALVATAGGIO_FALLISCE && C.acceptable(payload, chosen);
          }
          C.busy = false;
          C.render();
        });
      }, RITARDO_SALVATAGGIO);
    }

    /* Punto 6: si considera salvato solo quando Shopify restituisce entrambi gli
       attributi con i valori attesi. Un errore di rete viene ritentato prima di
       arrendersi, così un 429 passeggero non blocca un ordine. */
    static async persist(payload, chosen, revision) {
      const C = CodiceFiscaleValidator;
      for (let attempt = 1; attempt <= TENTATIVI_DI_SALVATAGGIO; attempt++) {
        try {
          const cart = await C.request('POST', payload, chosen);
          if (C.readAttribute(cart, KEY) !== payload ||
              C.readAttribute(cart, BILLING_KEY) !== chosen) {
            throw new Error('Attributi non persistiti');
          }
          return true;
        } catch (error) {
          if (revision !== C.revision) return false;
          if (attempt < TENTATIVI_DI_SALVATAGGIO) {
            await attesa(ATTESA_TRA_I_TENTATIVI[attempt - 1] ?? 1200);
            if (revision !== C.revision) return false;
          }
        }
      }
      return false;
    }

    /* Riallineamento con il carrello reale, al ritorno dalla bfcache. */
    static refresh() {
      const C = CodiceFiscaleValidator;
      clearTimeout(C.timer);
      const revision = ++C.revision;
      C.busy = true;
      C.failed = false;
      C.mode = 'read';
      C.render();

      C.queue = C.queue.then(async () => {
        if (revision !== C.revision) return;
        try {
          const cart = await C.request('GET');
          if (revision !== C.revision) return;
          C.value = normaliseCode(C.readAttribute(cart, KEY));
          C.billing = normaliseChoice(C.readAttribute(cart, BILLING_KEY));
          C.ready = C.acceptable(C.value, C.billing);
          C.saved = { value: C.ready ? C.value : null, billing: C.ready ? C.billing : null };
        } catch (error) {
          /* La lettura è fallita: non sappiamo più se il carrello sia allineato,
             quindi si torna a trattenere il checkout e si chiede di riconfermare,
             invece di fidarsi di uno stato mai riverificato. */
          if (revision === C.revision) {
            C.failed = true;
            C.ready = false;
            C.saved = { value: null, billing: null };
          }
        } finally {
          if (revision === C.revision) {
            C.busy = false;
            C.render();
          }
        }
      });
    }

    /* ----------------------------------------------------------------- render */

    static message() {
      const C = CodiceFiscaleValidator;
      const it = isItalian();

      if (C.busy) {
        if (C.mode === 'read') return it ? 'Verifica dei dati in corso…' : 'Checking your details…';
        return it ? 'Salvataggio in corso…' : 'Saving…';
      }

      if (C.failed && C.ready) {
        return it
          ? 'Non siamo riusciti a salvare i dati di fatturazione. Puoi procedere: ti scriveremo dopo l’ordine.'
          : 'We could not save your billing details. You can continue: we will contact you after your order.';
      }

      if (C.failed) {
        if (C.mode === 'read') {
          return it ? 'Non siamo riusciti a verificare i dati. Riseleziona il paese di fatturazione.'
                    : 'We could not check your details. Please select your billing country again.';
        }
        return it ? 'Salvataggio non riuscito. Riprova fra un istante.'
                  : 'Could not save. Please try again in a moment.';
      }

      if (C.ready) {
        return it ? 'Dati salvati. Puoi procedere al checkout.'
                  : 'Details saved. You can proceed to checkout.';
      }

      if (C.billing === ITALIA) {
        return it ? 'Inserisci il codice fiscale o la partita IVA per continuare.'
                  : 'Enter your Italian tax code or VAT number to continue.';
      }

      return it ? 'Seleziona il paese di fatturazione per continuare.'
                : 'Select your billing country to continue.';
    }

    static statusNode(root) {
      let node = root.querySelector('[data-fiscal-status]');
      if (!node) {
        node = document.createElement('p');
        node.setAttribute('data-fiscal-status', '');
        node.setAttribute('role', 'status');
        node.setAttribute('aria-live', 'polite');
        node.className = 'codice-fiscale__status';
        root.appendChild(node);
      }
      return node;
    }

    static render() {
      const C = CodiceFiscaleValidator;
      const showFields = C.billing === ITALIA;
      const value = C.value;
      const valid = C.valid(value);
      const message = C.message();

      for (const root of C.instances) {
        if (!C.hydrated(root)) continue;

        C.writeBilling(root, C.billing);

        for (const fields of root.querySelectorAll('[data-fiscal-fields]')) {
          if (fields.hidden !== !showFields) fields.hidden = !showFields;
        }

        const input = root.querySelector('[data-fiscal-input]');
        if (input) {
          /* Volutamente non si usa l'attributo required: il campo non ha name e
             non appartiene al form del carrello, quindi non darebbe alcun
             beneficio, e se lo snippet finisse dentro cart-form bloccherebbe in
             silenzio anche l'aggiornamento delle quantità. */
          setAttr(input, 'aria-required', showFields ? 'true' : 'false');
          /* Non si riscrive il campo mentre l'utente ci sta digitando dentro:
             assegnare .value sposterebbe il cursore in fondo a ogni tasto. */
          if (document.activeElement !== input && input.value !== value) input.value = value;
          setClass(input, 'codice-fiscale__input--valid', valid && C.ready);
          setClass(input, 'codice-fiscale__input--error', !!value && !valid);
          setAttr(input, 'aria-invalid', !!value && !valid ? 'true' : 'false');
        }

        setClass(root.querySelector('[data-fiscal-error]'), 'hidden', !value || valid);
        setText(C.statusNode(root), message);
      }

      C.buttons();

      /* Le mutazioni appena fatte sono nostre: si scartano, per non rientrare
         nell'observer in un ciclo infinito. */
      for (const root of C.instances) root._observer?.takeRecords();
    }

    /* --------------------------------------------------------------- pulsanti */

    /* I pulsanti da trattenere sono solo quelli del carrello a cui appartiene il
       componente. Con una query su tutto il documento, in un tema che tiene il
       cart drawer in ogni pagina, si spegnerebbero anche i pulsanti rapidi delle
       schede prodotto, che non passano nemmeno dal carrello. */
    static targets() {
      const C = CodiceFiscaleValidator;
      const scopes = new Set();

      for (const root of C.instances) {
        if (!root.isConnected) continue;
        /* Si risale al primo antenato che contiene il blocco CTA del carrello,
           marcato con data-fiscal-ctas, o in mancanza un pulsante di checkout:
           è l'ambito più stretto possibile. */
        let node = root.parentElement;
        while (node && node !== document.body && node !== document.documentElement) {
          const marked = node.querySelector(CTA_SCOPE_SELECTOR);
          if (marked) {
            scopes.add(marked);
            break;
          }
          if (node.querySelector(`${CHECKOUT_SELECTOR}, ${ACCEL_SELECTOR}`)) {
            scopes.add(node);
            break;
          }
          node = node.parentElement;
        }
      }

      const buttons = new Set();
      const boxes = new Set();
      for (const scope of scopes) {
        for (const button of scope.querySelectorAll(CHECKOUT_SELECTOR)) buttons.add(button);
        for (const box of scope.querySelectorAll(ACCEL_SELECTOR)) boxes.add(box);
      }
      return { buttons, boxes };
    }

    static unlock(node) {
      const mark = node.getAttribute(LOCK_ATTR);
      node.removeAttribute(LOCK_ATTR);
      if (mark === 'disabled' || mark === 'enabled') {
        const wasDisabled = mark === 'disabled';
        if (node.disabled !== wasDisabled) node.disabled = wasDisabled;
        setAttr(node, 'aria-disabled', wasDisabled ? 'true' : null);
      } else {
        const wasHidden = mark === 'hidden';
        if (node.hidden !== wasHidden) node.hidden = wasHidden;
      }
      setClass(node, 'codice-fiscale-blocked', false);
      setClass(node, 'codice-fiscale-blocked-accel', false);
    }

    static buttons() {
      const C = CodiceFiscaleValidator;
      const blocked = C.instances.size > 0 && !C.ready;
      const { buttons, boxes } = C.targets();

      /* Prima si libera tutto ciò che non deve più restare bloccato, compresi i
         nodi usciti dall'ambito: nessun pulsante può restare inchiodato. */
      for (const node of document.querySelectorAll(`[${LOCK_ATTR}]`)) {
        if (blocked && (buttons.has(node) || boxes.has(node))) continue;
        C.unlock(node);
      }

      if (!blocked) return;

      for (const button of buttons) {
        /* Lo stato originale si registra una sola volta, prima di toccarlo: con
           il carrello vuoto il tema lascia il pulsante disabilitato, e tale deve
           restare anche dopo lo sblocco. */
        if (!button.hasAttribute(LOCK_ATTR)) {
          button.setAttribute(LOCK_ATTR, button.disabled ? 'disabled' : 'enabled');
        }
        if (!button.disabled) button.disabled = true;
        setAttr(button, 'aria-disabled', 'true');
        setClass(button, 'codice-fiscale-blocked', true);
      }

      for (const box of boxes) {
        if (!box.hasAttribute(LOCK_ATTR)) {
          box.setAttribute(LOCK_ATTR, box.hidden ? 'hidden' : 'visible');
        }
        if (!box.hidden) box.hidden = true;
        setClass(box, 'codice-fiscale-blocked-accel', true);
      }
    }

    /* --------------------------------- guardie a livello di documento */

    static focusFirstGap() {
      const C = CodiceFiscaleValidator;
      let root = null;
      for (const node of C.instances) {
        if (C.hydrated(node) && node.isConnected) { root = node; break; }
      }
      if (!root) return;
      const target = C.billing === ITALIA
        ? root.querySelector('[data-fiscal-input]')
        : root.querySelector('[data-fiscal-billing]');
      if (!target) return;
      root.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      target.focus?.({ preventScroll: true });
    }

    static install() {
      const C = CodiceFiscaleValidator;
      if (C.installed) return;
      C.installed = true;

      const guard = (event) => {
        if (!C.instances.size || C.ready) return;

        let node = null;
        if (event.type === 'click') node = event.target;
        else if (event.type === 'submit') node = event.submitter;
        if (!node?.closest) return;

        /* Si blocca solo ciò che appartiene al carrello di questo componente.
           Gli altri invii del carrello (quantità, sconto, nota) passano. */
        const { buttons, boxes } = C.targets();
        let blocked = false;
        for (const target of buttons) {
          if (target === node || target.contains(node)) { blocked = true; break; }
        }
        if (!blocked && event.type === 'click') {
          for (const target of boxes) {
            if (target === node || target.contains(node)) { blocked = true; break; }
          }
        }
        if (!blocked) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        C.focusFirstGap();
      };

      document.addEventListener('click', guard, true);
      document.addEventListener('submit', guard, true);

      /* I pulsanti di checkout vengono ricreati dal rendering di sezione: dopo
         ogni mutazione si riapplica lo stato, una volta per frame. */
      new MutationObserver(() => {
        if (C.bodyFrame) return;
        C.bodyFrame = requestAnimationFrame(() => {
          C.bodyFrame = 0;
          C.buttons();
        });
      }).observe(document.documentElement, { childList: true, subtree: true });

      window.addEventListener('pageshow', (event) => {
        if (event.persisted && C.instances.size) C.refresh();
      });
    }
  }

  if (!customElements.get(TAG)) customElements.define(TAG, CodiceFiscaleValidator);

  /* Utile per una verifica rapida in Console. */
  window.AFPCodiceFiscale = CodiceFiscaleValidator;
})();